import { Button, Tooltip } from "@fluentui/react-components";
import {
  ArrowSyncCheckmarkFilled,
  ArrowSyncCheckmarkRegular,
  DeleteDismissFilled,
  DeleteDismissRegular,
} from "@fluentui/react-icons";
import {
  Cell,
  CellTemplate,
  Compatible,
  Uncertain,
  getCellProperty,
} from "@silevis/reactgrid";
import * as React from "react";

export interface ImportedInvoiceOptionsCell extends Cell {
  type: "importedinvoiceoptions";
  deleteFn?: any;
  matchFn?: any;
  rowId?: any;
  selected?: boolean;
  highlighted?: boolean;
  targetCount?: number; // rows the icons act on (> 1 on a ticked row with other ticked rows)
  t: any;
}

const optionalProperty = (cell: any, name: string, type: string) => {
  try {
    return getCellProperty(cell, name as any, type as any);
  } catch {
    return undefined;
  }
};

export class ImportedInvoiceOptionsCellTemplate
  implements CellTemplate<ImportedInvoiceOptionsCell>
{
  getCompatibleCell(
    uncertainCell: Uncertain<ImportedInvoiceOptionsCell>
  ): Compatible<ImportedInvoiceOptionsCell> {
    const t: any = getCellProperty(uncertainCell, "t", "function");
    const deleteFn = optionalProperty(uncertainCell, "deleteFn", "function");
    const matchFn = optionalProperty(uncertainCell, "matchFn", "function");
    const rowId = optionalProperty(uncertainCell, "rowId", "number");
    const selected = optionalProperty(uncertainCell, "selected", "boolean") ?? false;
    const highlighted = optionalProperty(uncertainCell, "highlighted", "boolean") ?? false;
    const targetCount = optionalProperty(uncertainCell, "targetCount", "number") ?? 1;

    return {
      ...uncertainCell,
      text: "",
      rowId,
      deleteFn,
      matchFn,
      value: NaN,
      selected,
      highlighted,
      targetCount,
      t,
    };
  }

  getClassName(
    cell: Compatible<ImportedInvoiceOptionsCell>,
    isInEditMode: boolean
  ): string {
    return (
      (cell.className ? cell.className : "") +
      (cell.selected ? " rg-selected" : "") +
      (cell.highlighted ? " highlighted-selected-row" : "")
    );
  }

  render(
    cell: Compatible<ImportedInvoiceOptionsCell>,
    isInEditMode: boolean,
    onCellChanged: (
      cell: Compatible<ImportedInvoiceOptionsCell>,
      commit: boolean
    ) => void
  ): React.ReactNode {
    return <OptionsCell cell={cell} />;
  }
}

interface DIProps {
  cell: Record<string, any>;
}

// filled icons and a count in the tooltip when the icon acts on several ticked rows
const OptionsCell: React.FC<DIProps> = ({ cell }) => {
  const many = (cell.targetCount ?? 1) > 1;
  return (
    <div style={{ width: "100%", textAlign: "center" }}>
      {cell.matchFn ? (
        <Tooltip
          content={
            many
              ? cell.t("pct.rematchImportedInvoicesSelected.label", { n: cell.targetCount })
              : cell.t("pct.rematchImportedInvoice.label")
          }
          relationship="label"
        >
          <Button
            data-help="imported-invoices-row-rematch"
            appearance="subtle"
            size="small"
            icon={many ? <ArrowSyncCheckmarkFilled /> : <ArrowSyncCheckmarkRegular />}
            onClick={() => {
              cell.matchFn(cell.rowId);
            }}
          ></Button>
        </Tooltip>
      ) : null}
      {cell.deleteFn ? (
        <Tooltip
          content={
            many
              ? cell.t("pct.deleteImportedInvoicesSelected.label", { n: cell.targetCount })
              : cell.t("pct.deleteImportedInvoice.label")
          }
          relationship="label"
        >
          <Button
            data-help="imported-invoices-row-delete"
            appearance="subtle"
            size="small"
            icon={many ? <DeleteDismissFilled /> : <DeleteDismissRegular />}
            onClick={() => {
              cell.deleteFn(cell.rowId);
            }}
          ></Button>
        </Tooltip>
      ) : null}
    </div>
  );
};
